import { useMemo } from 'react';

import { isRowApproved } from './approvalQueueModel';
import type { BlackoutChecklistRow } from './tabs/blackoutDates/types';
import type { BundledExperienceChecklistRow } from './tabs/bundledExperiences/types';
import type { CampaignChecklistRow } from './tabs/campaign/types';
import type { DatesRestrictionsChecklistRow } from './tabs/datesRestrictions/types';
import type { DealOptionsInclusionsChecklistRow } from './tabs/dealOptionsInclusions/types';
import type { EgsCapacityChecklistRow, EgsMarginChecklistRow } from './tabs/egs/types';
import type { SetupChecklistRow } from './tabs/setup/types';
import type { SurchargeTableRow, SurchargesChecklistRow } from './tabs/surcharges/types';
import type { TaxesPaymentsChecklistRow } from './tabs/taxesPayments/types';
import type { ManualOverrideState } from './types';

export function useChecklistSummary({
  baseSurchargesRows,
  campaignRows,
  datesRestrictionsRows,
  blackoutRows,
  bundledExperienceRows,
  taxesPaymentsRows,
  capacityRows,
  dealOptionsInclusionsRows,
  manualOverrides,
  marginRows,
  setupRows,
  surchargeTableRows,
}: {
  baseSurchargesRows: Array<SurchargesChecklistRow>;
  campaignRows: Array<CampaignChecklistRow>;
  datesRestrictionsRows: Array<DatesRestrictionsChecklistRow>;
  blackoutRows: Array<BlackoutChecklistRow>;
  bundledExperienceRows: Array<BundledExperienceChecklistRow>;
  taxesPaymentsRows: Array<TaxesPaymentsChecklistRow>;
  capacityRows: Array<EgsCapacityChecklistRow>;
  dealOptionsInclusionsRows: Array<DealOptionsInclusionsChecklistRow>;
  manualOverrides: ManualOverrideState;
  marginRows: Array<EgsMarginChecklistRow>;
  setupRows: Array<SetupChecklistRow>;
  surchargeTableRows: Array<SurchargeTableRow>;
}) {
  return useMemo(() => {
    const allRows = [
      ...setupRows,
      ...campaignRows,
      ...datesRestrictionsRows,
      ...bundledExperienceRows,
      ...marginRows,
      ...capacityRows,
      ...dealOptionsInclusionsRows,
      ...blackoutRows,
      ...taxesPaymentsRows,
      ...baseSurchargesRows,
      ...surchargeTableRows,
    ];
    const approvedCount = allRows.filter((row) => isRowApproved(row, manualOverrides)).length;
    const blockedCount = allRows.filter((row) => row.status === 'blocked').length;
    const totalCount = allRows.length;
    const needsApprovalCount = totalCount - approvedCount - blockedCount;

    return {
      approvedCount,
      needsApprovalCount,
      totalCount,
    };
  }, [
    baseSurchargesRows,
    campaignRows,
    datesRestrictionsRows,
    blackoutRows,
    bundledExperienceRows,
    taxesPaymentsRows,
    capacityRows,
    dealOptionsInclusionsRows,
    manualOverrides,
    marginRows,
    setupRows,
    surchargeTableRows,
  ]);
}
